import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Select,
  Stack,
  Text,
  Textarea,
  useClipboard,
} from "@chakra-ui/react";
import { ModalShell } from "../../../shared/ui";
import { formatarDataHora, parseDataISO } from "../../../shared/lib/date";
import { ROLES } from "../../auth/lib/roles";
import { createAccountSchema } from "../schemas/createAccountSchema";
import { useCreateAccountMutation } from "../hooks/useCreateAccountMutation";
import { creatableRoles, inviteMessage } from "../lib/accountCreation";
import { ROLE_LABEL } from "./UserBadges";

const EMPTY_VALUES = { email: "", fullName: "", role: ROLES.user };

const errorMessage = (error) =>
  error.response?.data?.erros?.[0]?.mensagem ?? error.response?.data?.mensagem ?? "Tente de novo em instantes.";

function CopyableField({ label, value, copyLabel, multiline = false }) {
  const { onCopy, hasCopied } = useClipboard(value);
  const Field = multiline ? Textarea : Input;
  return (
    <FormControl>
      <HStack justify="space-between" mb={1}>
        <FormLabel mb={0} fontSize="sm">
          {label}
        </FormLabel>
        <Button size="xs" onClick={onCopy}>
          {hasCopied ? "Copiado" : copyLabel}
        </Button>
      </HStack>
      <Field value={value} isReadOnly size="sm" bg="app-muted" rows={multiline ? 6 : undefined} />
    </FormControl>
  );
}

function CreatedAccount({ created, fullName }) {
  const { user, link, expires_at: expiresAt, invite_email_sent: inviteSent } = created;
  const validUntil = parseDataISO(expiresAt);
  return (
    <Stack spacing={4}>
      <Text fontSize="sm">
        A conta de <b>{user.email}</b> está pronta. Mande o link abaixo para a pessoa escolher a senha — você nunca
        precisa saber a senha dela.
      </Text>
      {inviteSent ? (
        <Alert status="info" borderRadius="md" fontSize="sm">
          <AlertIcon />
          <AlertDescription>Também enviamos um convite para {user.email} com este link.</AlertDescription>
        </Alert>
      ) : (
        <Alert status="warning" borderRadius="md" fontSize="sm">
          <AlertIcon />
          <AlertDescription>Não conseguimos enviar o convite por e-mail. Mande o link pelo WhatsApp.</AlertDescription>
        </Alert>
      )}
      <CopyableField label="Link para definir a senha" value={link} copyLabel="Copiar link" />
      <CopyableField
        label="Mensagem para o WhatsApp"
        value={inviteMessage({ fullName, email: user.email, link, expiresAt: validUntil })}
        copyLabel="Copiar mensagem"
        multiline
      />
      <Text fontSize="xs" color="app-muted-fg">
        Válido até {formatarDataHora(validUntil)} e só pode ser usado uma vez. Mande só para essa pessoa: quem tiver o
        link define a senha da conta. Se expirar, gere outro em Detalhes → Gerar link de redefinição de senha.
      </Text>
    </Stack>
  );
}

// Cadastro de conta pelo painel admin: o admin informa e-mail, nome e perfil e recebe um
// link de definição de senha para mandar ao médico (o admin nunca digita senha). O
// seletor só oferece os perfis que quem está logado pode criar; o backend confere de novo.
export default function CreateAccountModal({ isOpen, onClose, viewer }) {
  const mutation = useCreateAccountMutation();
  const [created, setCreated] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm({ resolver: zodResolver(createAccountSchema), defaultValues: EMPTY_VALUES });

  const close = () => {
    reset(EMPTY_VALUES);
    mutation.reset();
    setCreated(null);
    onClose();
  };

  const onSubmit = (values) => mutation.mutate(values, { onSuccess: setCreated });

  if (created) {
    return (
      <ModalShell isOpen={isOpen} onClose={close} title="Conta criada" cancelLabel="Fechar" size="lg">
        <CreatedAccount created={created} fullName={getValues("fullName")} />
      </ModalShell>
    );
  }

  const roles = creatableRoles(viewer);
  return (
    <ModalShell
      isOpen={isOpen}
      onClose={close}
      title="Criar conta"
      onSubmit={handleSubmit(onSubmit)}
      submitLabel="Criar conta"
      isSubmitting={mutation.isPending}
    >
      <Stack spacing={4}>
        <Text fontSize="sm" color="app-muted-fg">
          A conta já nasce com o e-mail verificado e o teste gratuito de 14 dias. Você recebe um link para a pessoa
          escolher a própria senha.
        </Text>
        {mutation.isError ? (
          <Alert status="error" borderRadius="md" fontSize="sm">
            <AlertIcon />
            <AlertDescription>{errorMessage(mutation.error)}</AlertDescription>
          </Alert>
        ) : null}

        <FormControl isInvalid={Boolean(errors.email)}>
          <FormLabel>E-mail</FormLabel>
          <Input type="email" inputMode="email" autoComplete="off" autoFocus {...register("email")} />
          <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={Boolean(errors.fullName)}>
          <FormLabel>Nome completo</FormLabel>
          <Input autoComplete="off" {...register("fullName")} />
          <FormErrorMessage>{errors.fullName?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={Boolean(errors.role)}>
          <FormLabel>Perfil</FormLabel>
          <Select {...register("role")}>
            {roles.map((role) => (
              <option key={role} value={role}>
                {ROLE_LABEL[role]}
              </option>
            ))}
          </Select>
          {roles.length === 1 ? (
            <FormHelperText>Só um superadministrador cria contas de administrador.</FormHelperText>
          ) : null}
          <FormErrorMessage>{errors.role?.message}</FormErrorMessage>
        </FormControl>
      </Stack>
    </ModalShell>
  );
}
